import { DndContext, KeyboardSensor, MouseSensor, TouchSensor, useSensor, useSensors } from "@dnd-kit/core";
import { runInAction } from "mobx";
import React from "react";
import { useTranslation } from "react-i18next";
import AlertContext, { type Alert, mkAlert } from "../../../../stores/contexts/Alert";
import type { GlobalId } from "../../../../stores/definitions/BaseRecord";
import type { Container, Location } from "../../../../stores/definitions/Container";
import type { HasLocation } from "../../../../stores/definitions/HasLocation";
import type { InventoryRecord } from "../../../../stores/definitions/InventoryRecord";
import useStores from "../../../../stores/use-stores";
import { type DroppableId, useContainerHelpers } from "./common";

type ContextArgs = {
  children: React.ReactNode;
  container: Container;

  /**
   * Note that keyboard support relies on each Dragger and Dropzone being
   * rendering inside of HTMLTableCellElements
   */
  supportKeyboard?: boolean;

  supportMultiple?: boolean;
};

export function Context({ children, container, supportKeyboard, supportMultiple }: ContextArgs): React.ReactNode {
  const { t } = useTranslation("inventory");
  const { addAlert, removeAlert } = React.useContext(AlertContext);
  const { getDestinationLocationForSourceLocation } = useContainerHelpers(container);
  const { moveStore } = useStores();
  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: {
      delay: 500,
      tolerance: 5,
    },
  });
  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: {
      delay: 500,
      tolerance: 5,
    },
  });
  const keyboardSensor = useSensor(KeyboardSensor, {
    /*
     * When in the keyboard-driven drag-and-drop mode, using the arrow keys
     * should move the content being dragged by the width/height of the current
     * HTMLTableCell that is being hovered over. This way, only a single tap is
     * required to move by a whole column/row.
     */
    coordinateGetter: (e: KeyboardEvent, { currentCoordinates }) => {
      const rect = (e.target as HTMLElement).closest("td")?.getBoundingClientRect();
      const width = rect?.width ?? 0;
      const height = rect?.height ?? 0;
      switch (e.code) {
        case "ArrowRight":
          return {
            ...currentCoordinates,
            x: currentCoordinates.x + width,
          };
        case "ArrowLeft":
          return {
            ...currentCoordinates,
            x: currentCoordinates.x - width,
          };
        case "ArrowDown":
          return {
            ...currentCoordinates,
            y: currentCoordinates.y + height,
          };
        case "ArrowUp":
          return {
            ...currentCoordinates,
            y: currentCoordinates.y - height,
          };
      }
    },
  });
  const sensors = useSensors(...[mouseSensor, touchSensor, ...(supportKeyboard ? [keyboardSensor] : [])]);

  /*
   * We can't support dragging-and-dropping multiple items in visual containers
   * because unlike grid containers the locations are not evenly spaced apart.
   * As such, we show a warning alert for the duration of the time that
   * multiple items are being dragged to explain this. By keeping a reference
   * to the displayed alert, we can remove it when the use releases the mouse
   * button.
   */
  const [multipleAlert, setMultipleAlert] = React.useState<Alert | null>(null);

  return (
    <DndContext
      sensors={sensors}
      onDragStart={(event) => {
        if (container.selectedLocations?.length === 0 && event.active.data.current) {
          // biome-ignore lint/suspicious/noExplicitAny: initial biome migration
          (event.active.data.current as any).location.toggleSelected(true);
        }
        if (!supportMultiple && (container.selectedLocations ?? []).length > 1) {
          const alert = mkAlert({
            variant: "warning",
            message: t("container.content.dragAndDrop.multipleItemsNotSupported"),
            isInfinite: true,
          });
          setMultipleAlert(alert);
          addAlert(alert);
        }
      }}
      onDragEnd={(event) => {
        if (multipleAlert) removeAlert(multipleAlert);
        setMultipleAlert(null);

        // event.over is null if the dragging is release when not hovering
        if (!event.over) return;

        if (!supportMultiple && (container.selectedLocations ?? []).length > 1) return;

        // biome-ignore lint/style/noNonNullAssertion: initial biome migration
        const [, col, row] = (event.over.id as string).match(/(\d+),(\d+)/)!;
        const destinationLocation = container.findLocation(parseInt(col, 10), parseInt(row, 10));
        if (!destinationLocation) throw new Error("Cannot find destination location.");
        // biome-ignore lint/suspicious/noExplicitAny: initial biome migration
        const sourceLocation = (event.active.data.current as any)?.location;
        if (!sourceLocation) throw new Error("Source location is undefined");

        if (!container.selectedLocations) throw new Error("Container must have some selected locations");
        const selectedLocations = container.selectedLocations;
        const sourceLocations: {
          [key: GlobalId]: {
            content: InventoryRecord & HasLocation;
            loc: Location;
          };
        } = Object.fromEntries(
          selectedLocations.map((l) => {
            if (!l.content) throw new Error("Selected location cannot be empty when moving.");
            if (!l.content.globalId) throw new Error("Content of selected location must have a Global ID when moving.");
            return [l.content.globalId, { content: l.content, loc: l }];
          }),
        );
        const destinationLocations: {
          [key: GlobalId]: {
            content: null | (InventoryRecord & HasLocation);
            loc: Location;
          };
        } = Object.fromEntries(
          selectedLocations.map((l) => {
            if (!l.content) throw new Error("Selected location cannot be empty when moving.");
            if (!l.content.globalId) throw new Error("Content of selected location must have a Global ID when moving.");
            const globalId = l.content.globalId;
            const compatibleEvent = {
              active: event.active
                ? {
                    data: {
                      current: event.active.data.current
                        ? {
                            relativeCoords: event.active.data.current.relativeCoords || [],
                          }
                        : null,
                    },
                  }
                : null,
              over: event.over ? { id: event.over.id as DroppableId } : null,
            };
            const dest = getDestinationLocationForSourceLocation(compatibleEvent, l);
            return [globalId, { content: dest.content, loc: dest }];
          }),
        );

        /*
         * If any of the destination locations contain content that is not
         * in the source locations then fail the move operation. Its fine
         * if one destination location contains content that is in another
         * source location because it will be moved itself by this move
         * operation.
         */
        if (
          Object.values(destinationLocations)
            .filter((l) => l.content?.globalId && !Object.keys(sourceLocations).includes(l.content.globalId))
            .some((l) => l.content)
        )
          return;

        try {
          if (destinationLocation !== sourceLocation) {
            const moveOperationParameters = Object.entries(sourceLocations).map(
              ([
                globalId,
                {
                  content: { id, type },
                },
              ]) => {
                return {
                  id,
                  type,
                  globalId,
                  parentContainers: [container.paramsForBackend],
                  parentLocation: destinationLocations[globalId].loc.paramsForBackend,
                };
              },
            );
            /*
             * Immediately update the data model so that the UI doesn't bounce
             * the table cell content back, which would make it look like there
             * was an error.
             */
            runInAction(() => {
              Object.entries(destinationLocations).forEach(([globalId, { loc }]) => {
                loc.content = sourceLocations[globalId].content;
              });
              const destCoords = Object.values(destinationLocations).map(({ loc }) => ({
                coordX: loc.coordX,
                coordY: loc.coordY,
              }));
              const sourceLocationsThatAreNotDestinations = Object.values(sourceLocations).filter(
                ({ loc }) => !destCoords.some(({ coordX, coordY }) => loc.coordX === coordX && loc.coordY === coordY),
              );
              sourceLocationsThatAreNotDestinations.forEach(({ loc }) => {
                loc.content = null;
              });
            });
            void moveStore.moveRecords(moveOperationParameters);
            /*
             * After successfully moving, we clear all selections. This is
             * because empty locations will otherwise be left selected,
             * which is hardly ever helpful to the user. Plus, if any
             * contents were left selected then the content context menu
             * would still be visible even though the location in which they
             * were located would no longer selected. To avoid any of this
             * confusion and complexity, we just clear all selections.
             */
            container.locations?.forEach((loc) => {
              loc.content?.toggleSelected(false);
              loc.toggleSelected(false);
            });
          }
        } catch (e) {
          /**
           * If there actually is an error, then put back the changes to
           * the data model so that they remain in sync with the server.
           * It is possible that the user has made further changes whilst
           * the network call was in flight, but this seems unlikely given
           * the single network call is pretty fast. If this does become an
           * issue, we may wish to disable drag-and-drop operations whilst
           * the last one is still being processed.
           */
          runInAction(() => {
            Object.values(sourceLocations).forEach(({ loc, content }) => {
              loc.content = content;
            });
            const sourceCoords = Object.values(sourceLocations).map(({ loc }) => ({
              coordX: loc.coordX,
              coordY: loc.coordY,
            }));
            const destinationLocationsThatAreNotSources = Object.values(destinationLocations).filter(
              ({ loc }) => !sourceCoords.some(({ coordX, coordY }) => loc.coordX === coordX && loc.coordY === coordY),
            );
            destinationLocationsThatAreNotSources.forEach(({ loc }) => {
              loc.content = null;
            });
          });
          console.error(e);
        }
      }}
    >
      {children}
    </DndContext>
  );
}
